import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Button from './Button/Button';
import ChevronDownIcon from './Icons/ChevronDownIcon';
import CheckIcon from './Icons/CheckIcon';
import { theme } from '../theme';
import Typography from './Typography';
import SortIcon from './Icons/SortIcon';

const DropdownContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const DropdownButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const DropdownIconWrapper = styled.div`
  transition: transform 0.2s ease;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const DropdownContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const DropdownMenu = styled.div`
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultSubtle};
  border-radius: ${({ theme }) => theme.radius.xs};
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);
  display: flex;
  flex-direction: column;
  margin: 4px 0 0;
  min-width: 120px;
  overflow: hidden;
  position: absolute;
  // left: 0;
  right: 0;
  top: 100%;
  z-index: 1100;
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.sizing.xs};
`;

const DropdownMenuItem = styled.div`
  align-items: center;
  border-radius: ${({ theme }) => theme.radius.xs};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.sizing.xs};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;

const MenuItemContent = styled(Typography)`
  flex: 1;
`;

const CheckIconWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 16px;
  height: 16px;
  opacity: ${({ $isSelected }) => $isSelected ? 1 : 0};
`;

const SortDropdown = ({
  options,
  selectedValue,
  onSelectionChange,
  label,
  placeholder
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const handleOptionClick = (optionValue) => {
    onSelectionChange(optionValue);
    setIsOpen(false);
  };

  const getButtonLabel = () => {
    const selectedOption = options.find(opt => opt.value === selectedValue);
    return selectedOption ? selectedOption.label : 'Ordenar por...';
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButton
        $variant="outline"
        $isOpen={isOpen}
        onClick={toggleDropdown}
        size="l"
        sizeTablet="l"
        sizeMobile="m"
      >
        <DropdownContent>
          <SortIcon fill={theme.colors.defaultStrong} width={20} height={20} />
          <Typography $variant="body-2-semibold" color={theme.colors.defaultStrong}>
            {getButtonLabel()}
          </Typography>
        </DropdownContent>
        <DropdownIconWrapper $isOpen={isOpen}>
          <ChevronDownIcon
            fill={theme.colors.defaultStrong}
            width={20}
            height={20}
          />
        </DropdownIconWrapper>
      </DropdownButton>

      {isOpen && (
        <DropdownMenu>
          <InnerWrapper>
            {options.map((option) => {
              const isSelected = selectedValue === option.value;
              return (
                <DropdownMenuItem
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                >
                  <MenuItemContent $variant="body-2-medium">
                    {option.label}
                  </MenuItemContent>
                  <CheckIconWrapper $isSelected={isSelected}>
                    <CheckIcon
                      fill={theme.colors.brandMain}
                      width={16}
                      height={16}
                    />
                  </CheckIconWrapper>
                </DropdownMenuItem>
              );
            })}
          </InnerWrapper>
        </DropdownMenu>
      )}
    </DropdownContainer>
  );
};

SortDropdown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  selectedValue: PropTypes.string.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

export default SortDropdown;